import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Edit3,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Image as ImageIcon,
  Video,
  Mic,
} from 'lucide-react';
import Layout from '../components/common/Layout';
import SharingInfoDisplay from '../components/common/SharingInfoDisplay';
import { getMilestone, getMilestones } from '../services/chaptersAPI';
import WisdomMomentsList from '../components/videoactivation/WisdomMomentsList';
import './MilestoneDetail.css';

/**
 * MilestoneDetailView - Enhanced view for a single chapter milestone
 * Matches the design from mockup-1-milestone-view.html
 */
function MilestoneDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [siblingMilestones, setSiblingMilestones] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchMilestone = async () => {
      try {
        setLoading(true);
        const data = await getMilestone(id);
        setMilestone(data);

        // Fetch sibling milestones from the same chapter for prev/next navigation
        if (data.chapter?.id) {
          try {
            const chapterMilestones = await getMilestones(data.chapter.id);
            // Sort by year, then by month
            const sorted = (chapterMilestones || []).sort((a, b) => {
              if (a.year !== b.year) return a.year - b.year;
              return (a.month || 0) - (b.month || 0);
            });
            setSiblingMilestones(sorted);
            // Find current index
            const idx = sorted.findIndex(m => m.id === id);
            setCurrentIndex(idx);
          } catch (siblingErr) {
            console.error('Error fetching sibling milestones:', siblingErr);
          }
        }
      } catch (err) {
        console.error('Error fetching milestone:', err);
        setError('Milestone not found');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMilestone();
    }
  }, [id]);

  // Audio playback handlers
  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setAudioProgress(progress);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAudioProgress(0);
  };

  // Navigation handlers for prev/next milestones
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < siblingMilestones.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      const prevMilestone = siblingMilestones[currentIndex - 1];
      navigate(`/my-life/milestones/${prevMilestone.id}`);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      const nextMilestone = siblingMilestones[currentIndex + 1];
      navigate(`/my-life/milestones/${nextMilestone.id}`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="milestone-main">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive mx-auto mb-4"></div>
                <p className="font-serif text-gray-600 text-lg">Loading milestone...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !milestone) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="milestone-main">
            <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
              <span className="text-5xl mb-4 block">😔</span>
              <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
                Milestone not found
              </h3>
              <p className="font-serif text-gray-500 text-base mb-4">
                We couldn't find this milestone.
              </p>
              <Link
                to="/my-life"
                className="text-fb-olive hover:underline font-semibold"
              >
                Back to My Life
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const {
    title,
    year,
    month,
    location,
    emoji,
    text,
    voice_url,
    voice_duration,
    photo_urls,
    chapter,
    created_at,
  } = milestone;

  // Format month
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonths = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const monthStr = month ? fullMonths[month - 1] : null;
  const dateStr = monthStr ? `${monthStr} ${year}` : year?.toString();

  // Format created date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Voice URL is returned directly from API as a full S3 URL
  const voiceUrl = voice_url || null;

  // Separate media by type - API returns array of {id, url, thumbnail_url, media_type}
  const allMedia = photo_urls || [];
  const imageMedia = allMedia.filter(p => p.media_type === 'image');
  const videoMedia = allMedia.filter(p => p.media_type === 'video');
  const audioMedia = allMedia.filter(p => p.media_type === 'audio');

  // For backward compatibility, photoUrls is just images
  const photoUrls = imageMedia.map(p => p.url).filter(Boolean);

  // Chapter info
  const chapterTitle = chapter?.title || 'Life Chapter';
  const chapterYearRange = chapter?.start_year
    ? `${chapter.start_year} – ${chapter.end_year || 'Present'}`
    : '';

  // Generate waveform bars for audio visualization
  const generateWaveformBars = () => {
    const bars = [];
    const heights = [8, 14, 20, 12, 24, 18, 10, 22, 16, 8, 20, 14, 26, 18, 10, 22, 12, 16, 8, 20, 14, 10, 18, 24, 12];
    for (let i = 0; i < heights.length; i++) {
      const isActive = (i / heights.length) * 100 <= audioProgress;
      bars.push(
        <div
          key={i}
          className={`wave-bar ${isActive ? 'active' : ''}`}
          style={{ height: `${heights[i]}px` }}
        />
      );
    }
    return bars;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-fb-cream">
        {/* Breadcrumb */}
        <div className="milestone-breadcrumb">
          <Link to="/my-life">My Life</Link>
          <span className="sep">›</span>
          {chapter && (
            <>
              <Link to={`/my-life/chapters/${chapter.id}`}>
                Chapter {chapter.number}: {chapterTitle}
              </Link>
              <span className="sep">›</span>
            </>
          )}
          <span style={{ color: '#2C2C2C' }}>{title}</span>
        </div>

        {/* Main Content */}
        <div className="milestone-main">
          {/* Header Card */}
          <div className="milestone-header-card">
            {chapter && (
              <div className="milestone-chapter-tag">
                <span>📖</span>
                Chapter {chapter.number}: "{chapterTitle}" · {chapterYearRange}
              </div>
            )}

            <h1 className="milestone-title">
              {emoji && <span className="mr-2">{emoji}</span>}
              {title}
            </h1>

            <div className="milestone-meta">
              {dateStr && (
                <div className="milestone-meta-item">
                  <Calendar size={16} />
                  <span>{dateStr}</span>
                </div>
              )}
              {location && (
                <div className="milestone-meta-item">
                  <MapPin size={16} />
                  <span>{location}</span>
                </div>
              )}
              {created_at && (
                <div className="milestone-meta-item">
                  <Edit3 size={16} />
                  <span>Created {formatDate(created_at)}</span>
                </div>
              )}
            </div>

            {/* Sharing Info - milestones are always owned by the current user */}
            <SharingInfoDisplay
              sharingScope={milestone.sharing_scope}
              originFamilyId={milestone.origin_family}
              sharedWithFamilies={milestone.shared_with_families || []}
              isOwner={true}
              className="mt-2"
            />

            <div className="milestone-header-actions">
              <button
                className="btn-icon"
                title="Edit milestone"
                onClick={() => navigate(`/my-life/milestones/${id}/edit`)}
              >
                <Edit3 size={16} />
              </button>
              <button className="btn-icon" title="More options">
                <MoreHorizontal size={16} />
              </button>
            </div>
          </div>

          {/* Photos Section */}
          {photoUrls.length > 0 ? (
            <div className="milestone-photos">
              <div className="photos-grid">
                <div className="photo-main">
                  <img src={photoUrls[0]} alt={`${title} - main photo`} />
                </div>
                <div className="photo-side">
                  {[1, 2, 3].map((idx) => (
                    <div key={idx} className="photo-thumb">
                      {photoUrls[idx] ? (
                        <>
                          <img src={photoUrls[idx]} alt={`${title} - photo ${idx + 1}`} />
                          {idx === 3 && photoUrls.length > 4 && (
                            <div className="photo-count-overlay">
                              +{photoUrls.length - 4} more
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="photo-placeholder">
                          <ImageIcon size={22} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="milestone-photos">
              <div className="photos-grid">
                <div className="photo-main">
                  <div className="photo-placeholder">
                    <ImageIcon size={28} />
                    <span>No photos added yet</span>
                  </div>
                </div>
                <div className="photo-side">
                  {[1, 2, 3].map((idx) => (
                    <div key={idx} className="photo-thumb">
                      <div className="photo-placeholder">
                        <ImageIcon size={22} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Videos Section */}
          {videoMedia.length > 0 && (
            <div className="milestone-videos" style={{ marginBottom: '20px' }}>
              <div className="story-label" style={{ marginBottom: '12px' }}>Videos</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {videoMedia.map((video, idx) => (
                  <div key={video.id || idx}>
                    <div style={{
                      background: '#fff',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid #E8E3D8'
                    }}>
                      <video
                        src={video.url}
                        controls
                        style={{ width: '100%', maxHeight: '300px', background: '#000' }}
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    {video.id && <WisdomMomentsList videoId={video.id} />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audio Files Section (from photos field, not voice_file) */}
          {audioMedia.length > 0 && (
            <div className="milestone-audios" style={{ marginBottom: '20px' }}>
              <div className="story-label" style={{ marginBottom: '12px' }}>Audio Files</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {audioMedia.map((audio, idx) => (
                  <div key={audio.id || idx} className="audio-attachment">
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#556B2F',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Mic size={18} color="#fff" />
                    </div>
                    <audio src={audio.url} controls style={{ flex: 1 }}>
                      Your browser does not support the audio tag.
                    </audio>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Story Section */}
          <div className="milestone-story">
            <div className="story-label">The Story</div>

            {text ? (
              <div className="story-text">
                {text.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            ) : (
              <div className="story-text" style={{ color: '#999999', fontStyle: 'italic' }}>
                No story text added yet. Click "Edit Milestone" to add your story.
              </div>
            )}

            {/* Audio Player */}
            {voiceUrl && (
              <div className="audio-attachment">
                <audio
                  ref={audioRef}
                  src={voiceUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleAudioEnded}
                />
                <button className="audio-play-btn" onClick={togglePlayback}>
                  {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <div className="audio-info">
                  <div className="audio-title">Voice recording</div>
                  <div className="audio-duration">
                    {formatDuration(voice_duration) || 'Audio'}
                  </div>
                </div>
                <div className="audio-waveform">
                  {generateWaveformBars()}
                </div>
              </div>
            )}

            {/* Location footer */}
            {location && (
              <div className="story-location">
                <MapPin size={15} />
                <span>{location}</span>
              </div>
            )}
          </div>

          {/* Reactions Section - Placeholder for Legacy Reactions integration */}
          <div className="milestone-reactions">
            <div className="reactions-row">
              <div className="reaction-chip">
                <span className="reaction-emoji">🕯️</span>
                <span className="reaction-count">0</span>
              </div>
              <div className="reaction-chip">
                <span className="reaction-emoji">🌱</span>
                <span className="reaction-count">0</span>
              </div>
              <div className="reaction-chip">
                <span className="reaction-emoji">🤗</span>
                <span className="reaction-count">0</span>
              </div>
              <div className="reaction-chip">
                <span className="reaction-emoji">🔄</span>
                <span className="reaction-count">0</span>
              </div>
              <div className="reaction-chip">
                <span className="reaction-emoji">🔖</span>
                <span className="reaction-count">0</span>
              </div>
              <div className="reactions-divider" />
              <button className="add-reaction-btn" title="Add reaction">
                +
              </button>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="milestone-footer">
            <button
              className="btn-edit"
              onClick={() => navigate(`/my-life/milestones/${id}/edit`)}
            >
              <Edit3 size={15} />
              Edit Milestone
            </button>
            <div className="footer-right">
              {hasPrevious && (
                <button
                  className="btn-secondary"
                  onClick={handlePrevious}
                >
                  <ChevronLeft size={15} />
                  Previous
                </button>
              )}
              {hasNext && (
                <button
                  className="btn-secondary"
                  onClick={handleNext}
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default MilestoneDetailView;
